import { useTranslation } from 'next-i18next';
import { type MouseEventHandler, type ReactNode, useCallback, useRef, useState } from 'react';
import { Box, ButtonV2 as Button } from '@bridebook/ui';
import SectionText from '@bridebook/web/app-shared/components/supplier/sections/section-text/section-text';
import type { IMessageSubcomponentProps } from './message';
import componentStyles from './message.style';
import { IconClass } from '@bridebook/ui/src/icons/constants';

interface IProps
  extends Pick<
    IMessageSubcomponentProps,
    'message' | 'response' | 'isVenue' | 'showFullResponse'
  > {}

const CLAMP_LINE_LIMIT = 4;
const LINE_HEIGHT = 20;

const ClampedMessage = ({ message, response, isVenue, showFullResponse }: IProps) => {
  const { t } = useTranslation('supplier');
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const expandOrCollapse = useCallback<MouseEventHandler<ReactNode>>((e) => {
    setIsCollapsed((v) => !v);
    e.stopPropagation();
    e.preventDefault();
  }, []);
  const hasReadMore = (textRef.current?.scrollHeight || 0) > CLAMP_LINE_LIMIT * LINE_HEIGHT;
  const styles = componentStyles(hasReadMore);

  return (
    <Box>
      <SectionText
        clampTo={isCollapsed ? CLAMP_LINE_LIMIT : 0}
        lineHeight={LINE_HEIGHT}
        fontSize={14}>
        <p ref={textRef}>{message}</p>
      </SectionText>
      {showFullResponse && !isCollapsed && (
        <Box style={styles.responseContainer}>
          <Box style={styles.responseWrapper}>
            <Box style={styles.venueResponse}>
              {isVenue ? t('response.venue') : t('response.supplier')}
            </Box>
            <Box style={styles.responseText}>{response?.message}</Box>
          </Box>
        </Box>
      )}
      {showFullResponse && !isCollapsed && (
        <Box style={styles.shortResponse}>
          {isVenue ? t('responded.venue') : t('responded.supplier')}
          <span className={IconClass.Approve} style={styles.approveIcon} />
        </Box>
      )}
      <Button
        variant="link"
        onClick={expandOrCollapse}
        size="large"
        styleWrapper={styles.expandOrCollapseButtonWrapper}
        styleButton={styles.expandOrCollapseButton}>
        {isCollapsed ? t('button.readMore') : t('button.seeLess')}
      </Button>
    </Box>
  );
};

export default ClampedMessage;
